import { useAppDispatch, useAppSelector } from "@app/hooks";
import { useUpdateSurveyMutation } from "@services/survey-core";
import {
  selectCurrentSurvey,
  selectError,
  selectIsLoading,
} from "@survey/surveySelectors";
import { useParams } from "react-router";

/**
 * Common hook for accessing survey state and update functionality.
 * Uses surveySelectors for consistent data access patterns.
 * Eliminates Redux pattern duplication across all survey-related components.
 * 
 * Includes sync checking to ensure Redux store survey ID matches URL parameter.
 * Returns isSynced flag to indicate if the store is synced with the URL.
 *
 * @returns Object containing survey data, loading/error states, dispatch function, updateSurvey mutation, and sync status
 */
const useSurveyState = () => {
  const { surveyId } = useParams();
  const survey = useAppSelector(selectCurrentSurvey);
  const isLoading = useAppSelector(selectIsLoading);
  const error = useAppSelector(selectError);
  const dispatch = useAppDispatch();
  const [updateSurvey] = useUpdateSurveyMutation();

  // Check if Redux store survey ID matches URL survey ID
  const isSynced = !surveyId || survey?.id === surveyId;

  return {
    survey,
    isLoading,
    error,
    dispatch,
    updateSurvey,
    isSynced,
    surveyId,
  };
};

export default useSurveyState;
